import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  MapPin,
  Bed,
  Bath,
  Maximize2,
  Shield,
  ChevronLeft,
  ChevronRight,
  Clock,
  Star,
  CalendarCheck,
  Home,
  Camera,
} from 'lucide-react';
import { Badge } from './ui/badge';
import {
  getOwnerConfirmationLabel,
  formatCurrency,
  formatDate,
  getMonthlyPriceLabel,
  getMonthlyReferenceAmount,
  hasValue,
} from '../lib/publicListingTruth';
import { getListingAreaDistanceLine } from '../lib/areaDistance';
import { resolveListingPhotoUrls } from '../lib/listingPhotoDisplay';
import ListingPhotoPending from './ListingPhotoPending';

export default function PropertyCard({ listing, baseRegistry = null, distanceUnit = 'km' }) {
  const {
    id,
    title,
    location_name,
    region,
    nearby_bases = [],
    hero_photo_url,
    photos = [],
    photo_count,
    bedrooms,
    bathrooms,
    sqm,
    tla_status,
    tla_eligible,
    availability_status,
    available_from,
    booked_until,
    rented_until,
    is_rented,
    days_until_available,
    is_early_access,
    auto_deactivated,
    amenities = [],
    avg_rating,
    review_count,
  } = listing;

  const { t } = useLanguage();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const AVAILABILITY_STATUS_LABELS = {
    AVAILABLE: t('listing.status.available', 'AVAILABLE'),
    UNAVAILABLE: t('listing.status.unavailable', 'UNAVAILABLE'),
    RESERVED: t('listing.status.reserved', 'RESERVED'),
    BOOKED: t('listing.status.booked', 'BOOKED'),
    RENTED: t('listing.status.rented', 'RENTED'),
    DEACTIVATED: t('listing.status.deactivated', 'DEACTIVATED'),
    COMING_SOON: t('listing.coming_soon_badge', 'COMING SOON'),
  };

  const daysUntil = Number.isFinite(days_until_available) ? days_until_available : null;
  const availabilityDate = formatDate(available_from || booked_until || rented_until);
  const isCurrentlyRented = is_rented === true;
  const isCurrentlyAvailable = availability_status === 'AVAILABLE' || daysUntil === 0;
  const isBookedButComingSoon = daysUntil !== null && daysUntil > 0 && daysUntil <= 90;
  const isDeactivated = auto_deactivated === true;

  // Badges render over photos — solid light surface keeps them legible on any image.
  const statusColors = {
    AVAILABLE: 'bg-white/95 text-emerald-700 border-emerald-300',
    UNAVAILABLE: 'bg-white/95 text-slate-700 border-slate-300',
    RENTED: 'bg-white/95 text-orange-700 border-orange-300',
    RESERVED: 'bg-white/95 text-amber-700 border-amber-300',
    BOOKED: 'bg-white/95 text-orange-700 border-orange-300',
    COMING_SOON: 'bg-white/95 text-amber-700 border-amber-300',
    DEACTIVATED: 'bg-white/95 text-red-700 border-red-300',
  };

  const photoUrls = resolveListingPhotoUrls({ photos, hero_photo_url });
  const hasUploadedPhotos = photoUrls.length > 0;
  const hasMultiplePhotos = photoUrls.length > 1;
  const priceValue = getMonthlyReferenceAmount(listing);
  const priceLabel = getMonthlyPriceLabel(listing);
  const ownerConfirmationLabel = getOwnerConfirmationLabel(listing, t);

  const areaDistanceLine = useMemo(
    () => getListingAreaDistanceLine(listing, baseRegistry, distanceUnit),
    [listing, baseRegistry, distanceUnit],
  );

  const nextPhoto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev + 1) % photoUrls.length);
  };

  const prevPhoto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev - 1 + photoUrls.length) % photoUrls.length);
  };

  return (
    <div
      className={`group block glass-card overflow-hidden transition-all duration-300 hover:border-military-gold/40 hover:shadow-xl hover:-translate-y-1 ${
        isDeactivated
          ? 'opacity-50 grayscale'
          : isCurrentlyRented && !isBookedButComingSoon
            ? 'opacity-85'
            : !isCurrentlyAvailable && !isBookedButComingSoon
              ? 'opacity-70'
              : ''
      }`}
      data-testid={`property-card-${id}`}
    >
      <Link to={`/listings/${id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          {hasUploadedPhotos ? (
            <img
              src={photoUrls[currentPhotoIndex]}
              alt={title}
              className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                isCurrentlyRented && !isBookedButComingSoon
                  ? 'grayscale-[35%]'
                  : !isCurrentlyAvailable && !isBookedButComingSoon
                    ? 'grayscale-[20%]'
                    : ''
              }`}
            />
          ) : (
            <ListingPhotoPending className="w-full h-full" />
          )}
          {hasUploadedPhotos && <div className="hero-overlay absolute inset-0" />}

          {isCurrentlyRented && availabilityDate && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-slate-100/90 backdrop-blur-sm px-4 py-3 rounded-lg text-center border border-orange-500/30 shadow-lg">
                <Home className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                <p className="text-xs text-orange-700 uppercase tracking-wide font-bold">{t('listing.currently_rented', 'Currently rented')}</p>
                <p className="text-slate-700 font-medium text-sm mt-1">{t('listing.available_again', 'Available again')}</p>
                <p className="text-orange-700 font-bold text-lg">{availabilityDate}</p>
                {daysUntil !== null && daysUntil > 0 && (
                  <p className="text-xs text-slate-600 mt-1">{t('common.in', 'in')} {daysUntil} {t('common.days', 'days')}</p>
                )}
              </div>
            </div>
          )}

          {availability_status === 'RESERVED' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-slate-100/90 backdrop-blur-sm px-4 py-3 rounded-lg text-center border border-amber-500/30 shadow-lg">
                <CalendarCheck className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-xs text-amber-700 uppercase tracking-wide font-bold">{t('listing.status.reserved', 'Reserved')}</p>
                {availabilityDate && (
                  <>
                    <p className="text-slate-700 font-medium text-sm mt-1">{t('listing.opens', 'Opens')}</p>
                    <p className="text-amber-700 font-bold text-lg">{availabilityDate}</p>
                    {daysUntil !== null && daysUntil > 0 && (
                      <p className="text-xs text-slate-600 mt-1">{t('common.in', 'in')} {daysUntil} {t('common.days', 'days')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!isCurrentlyRented && availability_status !== 'RESERVED' && isBookedButComingSoon && availabilityDate && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-slate-100/90 backdrop-blur-sm px-4 py-3 rounded-lg text-center border border-military-gold/30 shadow-lg">
                <CalendarCheck className="w-5 h-5 text-military-gold mx-auto mb-1" />
                <p className="text-xs text-slate-600 uppercase tracking-wide">{t('listing.available_from', 'Available from')}</p>
                <p className="text-military-gold font-bold text-lg">{availabilityDate}</p>
                {daysUntil !== null && <p className="text-xs text-slate-600 mt-1">{daysUntil} {t('common.days', 'days')}</p>}
              </div>
            </div>
          )}

          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {is_early_access && (
              <Badge className="bg-purple-500/20 text-purple-700 border border-purple-500/30 text-xs font-mono">
                <Star className="w-3 h-3 mr-1 fill-current" />
                {t('listing.early_access_badge', 'EARLY ACCESS')}
              </Badge>
            )}
            <Badge className={`${statusColors[isBookedButComingSoon ? 'COMING_SOON' : (availability_status || 'UNAVAILABLE')]} border text-xs font-mono uppercase`}>
              {isBookedButComingSoon
                ? t('listing.coming_soon_badge', 'COMING SOON')
                : (AVAILABILITY_STATUS_LABELS[availability_status] || AVAILABILITY_STATUS_LABELS.UNAVAILABLE)}
            </Badge>
          </div>

          {(tla_status === 'eligible' || tla_eligible) && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-white/95 text-amber-800 border border-amber-400 text-xs font-mono">
                <Shield className="w-3 h-3 mr-1" />
                {t('listing.tla_eligible_badge', 'TLA ELIGIBLE')}
              </Badge>
            </div>
          )}

          {priceValue != null && (
            <div className="absolute bottom-3 left-3">
              <div className="bg-slate-100/90 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                <span className="font-heading text-xl font-bold text-slate-800">{formatCurrency(priceValue)}</span>
                <span className="text-slate-600 text-sm">{t('listing.month', '/month')}</span>
                <div className="text-[10px] uppercase tracking-wide text-slate-600">
                  {priceLabel === 'known monthly total'
                    ? t('listing.price_known_monthly', 'known monthly total')
                    : t('listing.price_base_rent', 'base rent')}
                </div>
              </div>
            </div>
          )}

          {hasMultiplePhotos && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-100/70 rounded-full flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-100"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-100/70 rounded-full flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-100"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              <div className="absolute bottom-3 right-3">
                <div className="bg-slate-100/80 backdrop-blur-sm px-2 py-1 rounded-sm text-xs text-slate-700">
                  {currentPhotoIndex + 1} / {photoUrls.length} {t('listing.photos', 'photos')}
                </div>
              </div>
            </>
          )}
        </div>
      </Link>

      <Link to={`/listings/${id}`} className="block p-4">
        <h3 className="font-heading text-lg font-bold text-slate-800 mb-1 line-clamp-1 group-hover:text-signal-blue transition-colors">
          {title}
        </h3>

        <div className="flex items-center gap-1 text-slate-600 text-sm mb-1">
          <MapPin className="w-3.5 h-3.5" />
          <span>{location_name || t('listing.location_not_specified', 'Location not specified')}</span>
          {region && <span className="text-slate-500">• {region}</span>}
        </div>

        {areaDistanceLine ? (
          <div className="text-xs text-slate-500 mb-3" data-testid="listing-area-distance">
            {areaDistanceLine}
          </div>
        ) : nearby_bases.length > 0 ? (
          <div className="text-xs text-slate-500 mb-3">{t('listing.near', 'Near')} {nearby_bases.slice(0, 2).join(', ')}</div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          {avg_rating != null && review_count > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
              <Star className="w-3.5 h-3.5 text-military-gold fill-military-gold" />
              {avg_rating.toFixed(1)} ({review_count})
            </span>
          )}
          {hasValue(photo_count) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
              <Camera className="w-3.5 h-3.5 text-signal-blue" />
              {photo_count} {t('listing.photos', 'photos')}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-slate-600 bg-slate-100">
            <Clock className="w-3.5 h-3.5" />
            {ownerConfirmationLabel}
          </span>
        </div>

        <div className="flex items-center gap-4 py-3 border-t border-slate-200">
          <div className="flex items-center gap-1.5">
            <Bed className="w-4 h-4 text-slate-500" />
            <span className="font-mono text-sm text-slate-700">{bedrooms}</span>
            <span className="label-tactical">{t('listing.beds', 'beds')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Bath className="w-4 h-4 text-slate-500" />
            <span className="font-mono text-sm text-slate-700">{bathrooms}</span>
            <span className="label-tactical">{t('listing.bath', 'bath')}</span>
          </div>
          {sqm && (
            <div className="flex items-center gap-1.5">
              <Maximize2 className="w-4 h-4 text-slate-500" />
              <span className="font-mono text-sm text-slate-700">{sqm} m²</span>
            </div>
          )}
        </div>

        {amenities.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {amenities.slice(0, 3).map((amenity, i) => (
              <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-sm">
                {amenity}
              </span>
            ))}
            {amenities.length > 3 && (
              <span className="px-2 py-0.5 text-slate-500 text-xs">+{amenities.length - 3} {t('listing.more_label', 'more')}</span>
            )}
          </div>
        )}
      </Link>
    </div>
  );
}
